
<div class="h-full flex flex-col space-y-4">
    <!-- Header & Controls -->
    <div class="bg-white p-4 rounded-xl shadow-sm border border-slate-200 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div>
            <h2 class="text-xl font-bold text-gray-800 flex items-center gap-2">
                <i class="fa-solid fa-user-secret text-slate-600"></i> {{ 'backOffice.auditLogs.title' | translate }}
            </h2>
            <p class="text-xs text-gray-500 mt-1">{{ 'backOffice.auditLogs.subtitle' | translate }}</p>
        </div>
        
        <div class="flex gap-2">
             <button (click)="exportCsv()" class="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-slate-50 transition-colors flex items-center gap-2">
                <i class="fa-solid fa-file-csv text-green-600"></i> {{ 'backOffice.auditLogs.export' | translate }}
             </button>
        </div>
    </div>

    <!-- Filters -->
    <div class="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <!-- Date Preset -->
            <div class="bg-slate-100 p-1 rounded-lg flex">
                <button (click)="setDatePreset('today')" [class]="datePreset() === 'today' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'" class="flex-1 py-1.5 rounded-md text-xs font-bold transition-all">{{ 'backOffice.dashboard.filters.today' | translate }}</button>
                <button (click)="setDatePreset('7d')" [class]="datePreset() === '7d' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'" class="flex-1 py-1.5 rounded-md text-xs font-bold transition-all">{{ 'backOffice.dashboard.filters.last7days' | translate }}</button>
                <button (click)="setDatePreset('30d')" [class]="datePreset() === '30d' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'" class="flex-1 py-1.5 rounded-md text-xs font-bold transition-all">{{ 'backOffice.dashboard.filters.last30days' | translate }}</button>
            </div>

            <!-- User Filter -->
            <select [ngModel]="filter().userId" (ngModelChange)="updateUserFilter($event)" class="border-slate-300 rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500">
                <option value="all">{{ 'backOffice.auditLogs.filterUser.all' | translate }}</option>
                @for(u of users(); track u.uid) {
                    <option [value]="u.uid">{{ u.name }} ({{ u.role }})</option>
                }
            </select>

            <!-- Action Type -->
            <select [ngModel]="filter().actionType" (ngModelChange)="updateActionTypeFilter($event)" class="border-slate-300 rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500">
                <option value="ALL">{{ 'backOffice.auditLogs.filterAction.all' | translate }}</option>
                <option value="VIEW_ACCESS">{{ 'backOffice.auditLogs.filterAction.view' | translate }}</option>
                <option value="LOGIN">{{ 'backOffice.auditLogs.filterAction.login' | translate }}</option>
                <option value="CREATE">{{ 'backOffice.auditLogs.filterAction.create' | translate }}</option>
                <option value="UPDATE">{{ 'backOffice.auditLogs.filterAction.update' | translate }}</option>
                <option value="DELETE">{{ 'backOffice.auditLogs.filterAction.delete' | translate }}</option>
                <option value="SALE">{{ 'backOffice.auditLogs.filterAction.sale' | translate }}</option>
            </select>

            <!-- Search -->
            <div class="relative">
                <i class="fa-solid fa-search absolute left-3 top-3 text-slate-400 text-xs"></i>
                <input type="text" [ngModel]="filter().searchTerm" (ngModelChange)="updateSearchTerm($event)" [placeholder]="'backOffice.auditLogs.searchPlaceholder' | translate" class="w-full pl-8 border-slate-300 rounded-lg text-sm focus:ring-emerald-500 focus:border-emerald-500">
            </div>
        </div>
    </div>

    <!-- Table -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex-grow flex flex-col">
        <div class="overflow-y-auto flex-grow">
            <table class="min-w-full text-left text-sm text-slate-600">
                <thead class="bg-slate-50 text-slate-800 font-bold border-b border-slate-200 sticky top-0 shadow-sm">
                    <tr>
                        <th class="px-6 py-3 w-40">{{ 'backOffice.auditLogs.table.timestamp' | translate }}</th>
                        <th class="px-6 py-3 w-48">{{ 'backOffice.auditLogs.table.user' | translate }}</th>
                        <th class="px-6 py-3 w-32">{{ 'backOffice.auditLogs.table.action' | translate }}</th>
                        <th class="px-6 py-3">{{ 'backOffice.auditLogs.table.description' | translate }}</th>
                        <th class="px-6 py-3 w-20 text-center">{{ 'backOffice.auditLogs.table.data' | translate }}</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                    @for(log of logs(); track log.id) {
                        <tr class="hover:bg-slate-50 transition-colors">
                            <td class="px-6 py-3 whitespace-nowrap text-xs font-mono text-slate-500">
                                {{ log.created_at | date:'dd/MM/yy HH:mm:ss' }}
                            </td>
                            <td class="px-6 py-3">
                                <div class="font-bold text-slate-800">{{ log.user_name || 'System' }}</div>
                                <div class="text-xs text-slate-400">{{ log.user_role }}</div>
                            </td>
                            <td class="px-6 py-3">
                                <span class="px-2 py-1 rounded text-[10px] font-bold border uppercase tracking-wider whitespace-nowrap" [class]="getActionColor(log.action_type)">
                                    {{ log.action_type }}
                                </span>
                            </td>
                            <td class="px-6 py-3 text-slate-700">
                                {{ log.description }}
                            </td>
                            <td class="px-6 py-3 text-center">
                                @if(log.metadata) {
                                    <button (click)="toggleMetadata(log.id)" class="text-slate-400 hover:text-blue-600 transition-colors">
                                        <i class="fa-solid fa-code"></i>
                                    </button>
                                }
                            </td>
                        </tr>
                        @if(expandedLogId() === log.id && log.metadata) {
                            <tr class="bg-slate-50">
                                <td colspan="5" class="px-6 py-4 border-b border-slate-200">
                                    <div class="bg-slate-800 text-green-400 p-4 rounded-lg font-mono text-xs overflow-x-auto shadow-inner border border-slate-600">
                                        <pre>{{ log.metadata | json }}</pre>
                                    </div>
                                </td>
                            </tr>
                        }
                    } @empty {
                        <tr>
                            <td colspan="5" class="text-center py-20 text-slate-400">
                                <i class="fa-solid fa-clipboard-list text-4xl mb-3 opacity-30"></i>
                                <p>{{ 'backOffice.reports.empty' | translate }}</p>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
        
        <!-- Pagination -->
        <div class="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center text-xs font-medium text-slate-600">
            <span>{{ 'backOffice.auditLogs.pagination.total' | translate:{count: totalCount()} }}</span>
            <div class="flex gap-2">
                <button (click)="prevPage()" [disabled]="filter().page === 1" class="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50">Previous</button>
                <span class="self-center">Page {{ filter().page }}</span>
                <button (click)="nextPage()" [disabled]="filter().page * filter().pageSize >= totalCount()" class="px-3 py-1.5 bg-white border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50">Next</button>
            </div>
        </div>
    </div>
</div>
